import { SideMenuItem, SideMenuSection } from './pieces';
import { TeamMembers } from 'containers/TeamMembers';
import { Drive } from 'containers/Drive/Drive';
import { Assessments } from 'containers/Assessments';
import { Portfolios } from 'containers/Portfolios/Portfolios';
import { AllCompanies } from 'containers/AllCompanies';
import { Pai } from 'containers/CompanyPai';
import { Home } from 'containers/Home';
import { HomeIcon, NotificationIcon } from 'Tokens/Icons/Function';
import {
  CompanyIcon,
  DonutChartIcon,
  DriveIcon,
  ListIcon,
  TeamMembersIcon,
  PortfolioIcon,
  GroupIcon,
  HistogramIcon,
  BusinessUnitsIcon,
  TaskManagementIcon,
} from 'Tokens/Icons/Data';
import { GroupAssessments } from 'containers/GroupAssessment';
import { Groups } from 'containers/Groups';
import { EsrsAssessments } from 'containers/Esrs/EsrsAssessments';
import { IntegrationsRoutes } from 'containers/Integrations/IntegrationsRoutes';
import { TaskManagementPage } from 'containers/TaskList/components/TaskManagement.tsx';
import { HelpIcon } from 'Tokens/Icons/Status';
import { KnowledgeBaseSidebarButton } from 'Features/KnowledgeBase/KnowledgeBaseSidebarButton';
import { NotificationsSidebarButton } from 'Features/Notifications/NotificationsSidebarButton';

export enum AccessFlags {
  SUSTAINABILITY = 'sustainability-reporting',
  PAI = 'pai',
  TAXONOMY = 'assessments',
}

export const REGULAR_COMPANY_MODULES_SECTION_1: SideMenuItem[] = [
  {
    Icon: HomeIcon,
    title: 'Home',
    path: '/',
    key: 'home',
    section: SideMenuSection.primary,
    component: <Home />,
  },
  {
    Icon: NotificationIcon,
    path: '?showNotifications=true',
    title: 'Notifications',
    section: SideMenuSection.primary,
    component: <></>,
    key: 'notifications',
    CustomButton: NotificationsSidebarButton,
  },
  {
    Icon: TaskManagementIcon,
    path: 'task-management',
    title: 'Task management (beta)',
    section: SideMenuSection.primary,
    component: <TaskManagementPage />,
    key: 'taskManagement',
  },
];

export const REGULAR_COMPANY_MODULES_SECTION_2: SideMenuItem[] = [
  {
    Icon: HistogramIcon,
    path: 'sustainability-reporting',
    title: 'Sustainability reporting',
    section: SideMenuSection.secondary,
    component: <EsrsAssessments />,
    disabledTooltip:
      'The sustainability reporting module is not part of your current subscription. Contact us to upgrade your subscription and get access to this module!',
    key: AccessFlags.SUSTAINABILITY,
  },
  {
    Icon: DonutChartIcon,
    path: 'assessments',
    title: 'Assessments',
    section: SideMenuSection.secondary,
    component: <Assessments />,
    key: AccessFlags.TAXONOMY,
  },
  {
    Icon: ListIcon,
    path: 'pai',
    title: 'PAI and other indicators',
    section: SideMenuSection.secondary,
    component: <Pai />,
    disabledTooltip:
      'The PAI (Principle adverse impact) and other indicators module is not part of your current subscription. Contact us to upgrade your subscription and get access to this module!',
    key: AccessFlags.PAI,
  },
];

export const REGULAR_COMPANY_MODULES_SECTION_3: SideMenuItem[] = [
  {
    Icon: HelpIcon,
    path: '?withKnowledgeBase=true&slug=&article=',
    title: 'Help center',
    section: SideMenuSection.tertiary,
    component: <></>,
    key: 'helpCenter',
    CustomButton: KnowledgeBaseSidebarButton,
  },
];

export const PORTFOLIO_COMPANY_MODULES_SECTION_1: SideMenuItem[] = [
  {
    Icon: PortfolioIcon,
    path: 'portfolios',
    title: 'Portfolios',
    section: SideMenuSection.primary,
    component: <Portfolios />,
    key: 'portfolio',
  },
  {
    Icon: CompanyIcon,
    path: 'all-companies',
    title: 'All Companies',
    section: SideMenuSection.primary,
    component: <AllCompanies />,
    key: 'allCompanies',
  },
  {
    Icon: NotificationIcon,
    path: '?showNotifications=true',
    title: 'Notifications',
    section: SideMenuSection.primary,
    component: <></>,
    key: 'notifications',
    CustomButton: NotificationsSidebarButton,
  },
];

export const GROUP_COMPANY_MODULES_SECTION_1: SideMenuItem[] = [
  {
    Icon: HomeIcon,
    title: 'Home',
    path: '/',
    key: 'home',
    section: SideMenuSection.primary,
    component: <Home />,
  },
  {
    Icon: NotificationIcon,
    path: '?showNotifications=true',
    title: 'Notifications',
    section: SideMenuSection.primary,
    component: <></>,
    key: 'notifications',
    CustomButton: NotificationsSidebarButton,
  },
  {
    Icon: TaskManagementIcon,
    path: 'task-management',
    title: 'Task management (beta)',
    section: SideMenuSection.primary,
    component: <TaskManagementPage />,
    key: 'taskManagement',
  },
];

export const GROUP_COMPANY_MODULES_SECTION_2: SideMenuItem[] = [
  {
    Icon: HistogramIcon,
    path: 'sustainability-reporting',
    title: 'Sustainability reporting',
    section: SideMenuSection.secondary,
    component: <EsrsAssessments />,
    disabledTooltip:
      'The sustainability reporting module is not part of your current subscription. Contact us to upgrade your subscription and get access to this module!',
    key: AccessFlags.SUSTAINABILITY,
  },
  {
    Icon: DonutChartIcon,
    path: 'assessments',
    title: 'Assessments',
    section: SideMenuSection.secondary,
    component: <GroupAssessments />,
    key: AccessFlags.TAXONOMY,
  },
  {
    Icon: GroupIcon,
    path: 'groups',
    title: 'Groups',
    section: SideMenuSection.secondary,
    component: <Groups />,
    key: 'groups',
  },
];

export const GENERAL_MODULES: SideMenuItem[] = [
  {
    Icon: TeamMembersIcon,
    path: 'team-members',
    title: 'Team Members',
    section: SideMenuSection.general,
    component: <TeamMembers />,
    key: 'teamMembers',
  },
  {
    Icon: GroupIcon,
    path: 'groups',
    title: 'Groups',
    section: SideMenuSection.general,
    component: <Groups />,
    key: 'groups',
  },
  {
    Icon: DriveIcon,
    path: 'drive',
    title: 'Drive',
    section: SideMenuSection.general,
    component: <Drive />,
    disabledTooltip:
      'Uploading and management of files is currently not enabled for this company. Contact us in the chat if you have any questions.',
    key: 'drive',
  },
  {
    Icon: BusinessUnitsIcon,
    path: 'integrations',
    title: 'Integrations',
    section: SideMenuSection.general,
    component: <IntegrationsRoutes />,
    key: 'integrations',
  },
];

export const ALL_MODULES: SideMenuItem[] = [
  ...REGULAR_COMPANY_MODULES_SECTION_1,
  ...REGULAR_COMPANY_MODULES_SECTION_2,
  ...REGULAR_COMPANY_MODULES_SECTION_3,
  ...PORTFOLIO_COMPANY_MODULES_SECTION_1,
  ...GROUP_COMPANY_MODULES_SECTION_1,
  ...GROUP_COMPANY_MODULES_SECTION_2,
  ...GENERAL_MODULES,
];
